/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, ComponentProps, Ref, Fragment, JSX } from 'preact';
import { BaseButton } from '../UNSAFE_BaseButton';
import { ProgressButtonLabelLayout } from '../UNSAFE_ProgressButtonLabelLayout';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { forwardRef, ElementRef } from 'preact/compat';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { TestIdProps } from '../hooks/UNSAFE_useTestId';
import { useTooltip } from '../hooks/UNSAFE_useTooltip';
import { processGlobals, ButtonIntrinsicProps } from '../utils/UNSAFE_buttonUtils';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import translations from '#resources/nls/en/bundle';
import { LiveRegion } from '#UNSAFE_LiveRegion';
import { useProgressLoadingState } from '#hooks/PRIVATE_useProgressLoadingState';

type ButtonHandle = ElementRef<typeof BaseButton>;

type IntrinsicProps = Pick<UnpackSignals<JSX.HTMLAttributes<HTMLElement>>, 'autofocus'>;
type WidthInterpolationProps = Pick<DimensionProps, 'width'>;
type BaseButtonProps = ComponentProps<typeof BaseButton>;

type Props = IntrinsicProps &
  ButtonIntrinsicProps &
  Pick<BaseButtonProps, 'aria-describedby'> &
  WidthInterpolationProps &
  TestIdProps & {
    /**
     * The startIcon is the button's start icon.
     */
    startIcon?: ComponentChildren;

    /**
     * Specifies that a quick indeterminate action is taking place, making the button read-only.
     */
    isLoading?: boolean;

    /**
     * Specifies that the button element should be disabled.
     */
    isDisabled?: boolean;

    /**
     * Specifes the button type
     */
    type?: 'button' | 'submit';

    /**
     * label - button label, used for accessibility if no override
     */
    label?: string;

    /**
     * Indicates in what states the button has chrome (background and border).
     *     "borderless"': "Borderless buttons are a more prominent variation. Borderless
     * buttons are useful for supplemental actions that require minimal emphasis.",
     *     "outlined"': "Outlined buttons are salient, but lighter weight than
     * solid buttons. Outlined buttons are useful for secondary actions.",
     *     "solid"': "Solid buttons stand out, and direct the user's attention to the
     *  most important actions in the UI.",
     *     "callToAction"': "A Call To Action (CTA) button guides the user to take or
     * complete the action that is the main goal of the page or page section. There
     * should only be one CTA button on a page at any given time.",
     */
    variant?: 'borderless' | 'outlined' | 'solid' | 'callToAction';

    /**
     * The size indicates how large the button is rendered.
     */
    size?: 'sm' | 'md' | 'lg';

    /**
     * Edge can be used to specify that the button is intended to go at the bottom of
     * a region, stretching from left to right.
     */
    edge?: ComponentProps<typeof BaseButton>['edge'];

    /**
     * Triggered when a button is clicked, whether by keyboard, mouse, or touch events.
     */
    onAction?: ComponentProps<typeof BaseButton>['onAction'];

    /**
     * Display indicates whether only the label or all elements should be rendered by the button.
     */
    display?: 'label' | 'all';

    /**
     * tooltip - overrides the aria-label for the tooltip
     */
    tooltip?: string;
  };
/**
 * A Progress button is a push button that can show progress.
 */
export const ProgressButton = forwardRef(
  (
    {
      variant = 'outlined',
      isLoading = false,
      isDisabled = false,
      size = 'md',
      display = 'all',
      type: buttonType = 'button',
      startIcon,
      edge,
      testId,
      onAction,
      label = '',
      tooltip,
      'aria-describedby': ariaDescribedBy,
      width,
      ...props
    }: Props,
    ref: Ref<ButtonHandle>
  ) => {
    const { globalEventProps } = processGlobals(props);
    const isAdjustedLoading = isDisabled ? false : isLoading;
    const progressStatus = translations.accButtonProgressStatus({
      label: label
    });
    const progressComplete = translations.accButtonProgressComplete({
      label: label
    });

    const showTooltip = isAdjustedLoading && (display == 'label' || !startIcon);
    const tooltipLabel = showTooltip ? (tooltip ? tooltip : label) : tooltip;

    const { tooltipContent, tooltipProps } = useTooltip({
      text: tooltipLabel,
      isDisabled
    });

    const { loaded } = useProgressLoadingState(isLoading);

    const labelOnly = !startIcon || display === 'label';
    const elementDetails = {
      type: 'button' as const,
      buttonType: buttonType
    };
    const content = (
      <ProgressButtonLabelLayout
        display={display}
        isLoadingContrast={variant === 'callToAction'}
        startIcon={startIcon}
        size={size}
        isLoading={isAdjustedLoading}>
        {label}
      </ProgressButtonLabelLayout>
    );

    /* For a11y, we use ignore aria-label during loading time */
    const { 'aria-describedby': adb, ...ttProps } = tooltipProps;

    return (
      <Fragment>
        <BaseButton
          {...mergeProps(ttProps, globalEventProps)}
          ref={ref}
          elementDetails={elementDetails}
          isDisabled={isDisabled}
          isReadOnly={isAdjustedLoading}
          size={size}
          width={width}
          edge={edge}
          variant={variant}
          styling={
            labelOnly
              ? isAdjustedLoading
                ? ['default', 'hiddenLabel', 'noHoverActive']
                : ['default']
              : isAdjustedLoading
              ? ['noHoverActive', 'min']
              : ['min']
          }
          onAction={onAction}
          aria-label={label}
          testId={testId}>
          {content}
        </BaseButton>
        {tooltipContent}
        <LiveRegion type="assertive">
          {loaded ? progressComplete : isLoading ? progressStatus : ''}
        </LiveRegion>
      </Fragment>
    );
  }
);

ProgressButton.displayName = 'ProgressButton';
